// Nadrama <https://nadrama.com>
// Copyright Nadrama Pty Ltd
// All rights reserved.
import {
  Show,
  createContext,
  useContext,
  type Accessor,
  type ParentProps,
} from "solid-js";
import { createQuery } from "@tanstack/solid-query";
import type { AccountEntitlements } from "./api/generated";
import { getEntitlements } from "./api/generated";
import { authSlotHeaders } from "./api/runtime";
import { expiredSession } from "./auth/state";

type EntitlementKey = keyof AccountEntitlements;
const EntitlementsContext = createContext<Accessor<AccountEntitlements>>();

export function EntitlementsProvider(
  p: ParentProps<{ slot: number; account: string }>,
) {
  const query = createQuery(() => ({
    queryKey: [p.slot, p.account, "entitlements"],
    queryFn: async () =>
      (
        await getEntitlements({
          throwOnError: true,
          responseStyle: "fields",
          path: { account: p.account },
          headers: authSlotHeaders(p.slot),
        })
      ).data,
  }));
  return (
    <>
      <Show when={query.isPending}>
        <p>Loading account access…</p>
      </Show>
      <Show when={query.isError && expiredSession() === undefined}>
        <div role="alert" class="alert alert-error">
          Unable to load account access. Please try again.
        </div>
      </Show>
      <Show when={query.data} keyed>
        {(entitlements) => (
          <EntitlementsContext.Provider value={() => entitlements}>
            {p.children}
          </EntitlementsContext.Provider>
        )}
      </Show>
    </>
  );
}

export function useEntitlement(key: EntitlementKey): Accessor<boolean> {
  const entitlements = useContext(EntitlementsContext);
  if (!entitlements) throw new Error("EntitlementsProvider is missing");
  return () => entitlements()[key];
}

export function AccessNotPermitted(p: { children?: string }) {
  return (
    <div
      role="note"
      class="access-not-permitted panel border-dashed p-6 text-slate-600"
    >
      <h3 class="font-semibold text-slate-900">Access not permitted</h3>
      <p class="mt-1 text-sm">
        {p.children ??
          "You don’t have permission to view this section. Ask an account administrator if you need access."}
      </p>
    </div>
  );
}
